import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { 
  Upload, 
  Image as ImageIcon, 
  Type, 
  Palette, 
  Move, 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  X, 
  Loader2,
  ShoppingCart,
  Eye,
  FileText,
  Truck,
  TestTube2,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Trash2,
  GripVertical,
  FlipHorizontal,
  FlipVertical,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

const PRESET_COLORS = [
  '#1e3a5f', // Navy
  '#d4af37', // Gold
  '#000000', // Black
  '#ffffff', // White
  '#dc2626', // Red
  '#16a34a', // Green
  '#2563eb', // Blue
  '#9333ea', // Purple
  '#ea580c', // Orange
  '#0d9488', // Teal
];

const TEXT_SIZES = [
  { value: 'small', label: 'Small', fontSize: 14 },
  { value: 'medium', label: 'Medium', fontSize: 20 },
  { value: 'large', label: 'Large', fontSize: 28 },
];

export default function GiftCustomizationModal({ 
  isOpen, 
  onClose, 
  gift,
  onOrderComplete
}) {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const logoRef = useRef(null);
  const textRef = useRef(null);
  
  // Step management (simplified to 3 steps with interactive editor)
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3; // 1: Design Editor, 2: Delivery Details, 3: Confirm
  
  // Logo state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [processedLogoUrl, setProcessedLogoUrl] = useState(null); // Canvas-processed logo
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Logo transform state (Vistaprint-style)
  const [logoPosition, setLogoPosition] = useState({ x: 50, y: 50 }); // percentage
  const [logoScale, setLogoScale] = useState(30); // percentage of container width
  const [logoRotation, setLogoRotation] = useState(0); // degrees
  const [logoFlipH, setLogoFlipH] = useState(false); // horizontal flip
  const [logoFlipV, setLogoFlipV] = useState(false); // vertical flip
  const [logoTransparent, setLogoTransparent] = useState(true); // remove white background
  const [logoColor, setLogoColor] = useState('original'); // 'original', 'black', 'white'
  
  // Process logo with canvas when color or transparency changes
  useEffect(() => {
    if (!logoPreview || logoFile?.type === 'application/pdf') {
      setProcessedLogoUrl(null);
      return;
    }
    
    const processLogo = async () => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw the original image
        ctx.drawImage(img, 0, 0);
        
        // Get image data for pixel manipulation
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Process each pixel
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          
          // Check if pixel is close to white (threshold for white detection)
          const isWhite = r > 240 && g > 240 && b > 240;
          
          if (logoTransparent && isWhite) {
            // Make white pixels transparent
            data[i + 3] = 0; // Set alpha to 0
          } else if (a > 0) {
            // For non-transparent, non-white pixels, apply color
            if (logoColor === 'black') {
              data[i] = 0;     // R
              data[i + 1] = 0; // G
              data[i + 2] = 0; // B
            } else if (logoColor === 'white') {
              data[i] = 255;     // R
              data[i + 1] = 255; // G
              data[i + 2] = 255; // B
            }
            // 'original' keeps the original colors
          }
        }
        
        // Put the processed data back
        ctx.putImageData(imageData, 0, 0);
        
        // Convert to data URL
        setProcessedLogoUrl(canvas.toDataURL('image/png'));
      };
      
      img.src = logoPreview;
    };
    
    processLogo();
  }, [logoPreview, logoColor, logoTransparent, logoFile?.type]);
  
  // Text customization state
  const [companyName, setCompanyName] = useState('');
  const [textPosition, setTextPosition] = useState({ x: 50, y: 85 }); // percentage
  const [textColor, setTextColor] = useState('#1e3a5f');
  const [textSize, setTextSize] = useState('medium');
  
  // Branding color
  const [brandingColor, setBrandingColor] = useState('#1e3a5f');
  
  // Dragging state
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [isDraggingText, setIsDraggingText] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Active element for controls
  const [activeElement, setActiveElement] = useState(null); // 'logo' or 'text'
  
  // Delivery details
  const [quantity, setQuantity] = useState(gift?.min_order_quantity || 1);
  const [deliveryName, setDeliveryName] = useState('');
  const [deliveryPhone, setDeliveryPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  
  // Test mode
  const [testMode, setTestMode] = useState(false);
  const [customAmount, setCustomAmount] = useState(1);
  
  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    if (gift && isOpen) {
      setQuantity(gift.min_order_quantity || 1);
    }
  }, [gift, isOpen]);
  
  // Handle drag start for logo
  const handleLogoMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
    setActiveElement('logo');
    const rect = editorRef.current?.getBoundingClientRect();
    if (rect) {
      setDragStart({
        x: e.clientX - (logoPosition.x / 100 * rect.width),
        y: e.clientY - (logoPosition.y / 100 * rect.height)
      });
    }
  };
  
  // Handle drag start for text
  const handleTextMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingText(true);
    setActiveElement('text');
    const rect = editorRef.current?.getBoundingClientRect();
    if (rect) {
      setDragStart({
        x: e.clientX - (textPosition.x / 100 * rect.width),
        y: e.clientY - (textPosition.y / 100 * rect.height)
      });
    }
  };
  
  // Handle mouse move
  const handleMouseMove = useCallback((e) => {
    const rect = editorRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    if (isDraggingLogo) {
      const newX = ((e.clientX - dragStart.x) / rect.width) * 100;
      const newY = ((e.clientY - dragStart.y) / rect.height) * 100;
      setLogoPosition({
        x: Math.max(5, Math.min(95, newX)),
        y: Math.max(5, Math.min(95, newY))
      });
    }
    
    if (isDraggingText) {
      const newX = ((e.clientX - dragStart.x) / rect.width) * 100;
      const newY = ((e.clientY - dragStart.y) / rect.height) * 100;
      setTextPosition({
        x: Math.max(10, Math.min(90, newX)),
        y: Math.max(10, Math.min(95, newY))
      });
    }
  }, [isDraggingLogo, isDraggingText, dragStart]);
  
  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDraggingLogo(false);
    setIsDraggingText(false);
  }, []);
  
  // Add/remove event listeners
  useEffect(() => {
    if (isDraggingLogo || isDraggingText) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingLogo, isDraggingText, handleMouseMove, handleMouseUp]);
  
  // Touch handlers for mobile
  const handleTouchStart = (e, type) => {
    e.preventDefault();
    const touch = e.touches[0];
    if (type === 'logo') {
      setIsDraggingLogo(true);
      setActiveElement('logo');
    } else {
      setIsDraggingText(true);
      setActiveElement('text');
    }
    const rect = editorRef.current?.getBoundingClientRect();
    if (rect) {
      const position = type === 'logo' ? logoPosition : textPosition;
      setDragStart({
        x: touch.clientX - (position.x / 100 * rect.width),
        y: touch.clientY - (position.y / 100 * rect.height)
      });
    }
  };
  
  const handleTouchMove = useCallback((e) => {
    const rect = editorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const touch = e.touches[0];
    
    if (isDraggingLogo) {
      const newX = ((touch.clientX - dragStart.x) / rect.width) * 100;
      const newY = ((touch.clientY - dragStart.y) / rect.height) * 100;
      setLogoPosition({
        x: Math.max(5, Math.min(95, newX)),
        y: Math.max(5, Math.min(95, newY))
      });
    }
    
    if (isDraggingText) {
      const newX = ((touch.clientX - dragStart.x) / rect.width) * 100;
      const newY = ((touch.clientY - dragStart.y) / rect.height) * 100;
      setTextPosition({
        x: Math.max(10, Math.min(90, newX)),
        y: Math.max(10, Math.min(95, newY))
      });
    }
  }, [isDraggingLogo, isDraggingText, dragStart]);
  
  const handleTouchEnd = useCallback(() => {
    setIsDraggingLogo(false);
    setIsDraggingText(false);
  }, []);
  
  useEffect(() => {
    if (isDraggingLogo || isDraggingText) {
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      return () => {
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDraggingLogo, isDraggingText, handleTouchMove, handleTouchEnd]);
  
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, GIF, WEBP or PDF file');
      return;
    }
    
    // Validate size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setLogoFile(file);
    
    // Create preview for images (not PDF)
    if (file.type !== 'application/pdf') {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
        // Reset logo position and transforms when new logo is uploaded
        setLogoPosition({ x: 50, y: 50 });
        setLogoScale(30);
        setLogoRotation(0);
        setLogoFlipH(false);
        setLogoFlipV(false);
        setActiveElement('logo');
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview('/pdf-placeholder.png');
    }
    
    // Upload to server
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.uploadLogo(formData);
      setUploadedLogoUrl(response.data.logo_url);
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload logo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };
  
  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setUploadedLogoUrl(null);
    setActiveElement(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleSubmitOrder = async () => {
    if (!user) {
      toast.error('Please login to place an order');
      return;
    }
    
    // Validate delivery details
    if (!deliveryName || !deliveryPhone || !deliveryAddress) {
      toast.error('Please fill in all delivery details');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare order data with position info
      const orderData = {
        gift_id: gift.id,
        quantity: parseInt(quantity),
        delivery_name: deliveryName,
        delivery_phone: deliveryPhone,
        delivery_address: deliveryAddress,
        company_email: companyEmail || null,
        special_instructions: specialInstructions || null,
        branding_color: brandingColor,
      };
      
      // Add logo customization if uploaded
      if (uploadedLogoUrl) {
        orderData.logo_customization = {
          logo_url: uploadedLogoUrl,
          position: `${logoPosition.x.toFixed(1)}%,${logoPosition.y.toFixed(1)}%`,
          size: `${logoScale}%`,
        };
      }
      
      // Add text customization if company name provided
      if (companyName) {
        orderData.text_customization = {
          company_name: companyName,
          font_size: textSize,
          color: textColor,
        };
      }
      
      // Create the customized order
      const response = await api.createCustomizedGiftOrder(orderData);
      toast.success('Order created successfully!');
      
      // Calculate payment amount
      let amount = response.data.total_amount;
      if (testMode && customAmount > 0) {
        amount = customAmount;
        toast.info(`Test Mode: Using custom amount ₹${customAmount}`);
      }
      
      // Create Razorpay order
      const razorpayResponse = await api.createRazorpayOrder({
        gift_order_id: response.data.id,
        amount: amount,
        payment_type: 'full',
      });
      
      // Initialize Razorpay checkout
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: razorpayResponse.data.amount,
        currency: razorpayResponse.data.currency,
        name: 'My Corporate Hub',
        description: `Customized Gift Order - ${gift.name}`,
        image: 'https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png',
        order_id: razorpayResponse.data.order_id,
        handler: async function (razorpayResult) {
          try {
            await api.verifyRazorpayPayment({
              razorpay_order_id: razorpayResult.razorpay_order_id,
              razorpay_payment_id: razorpayResult.razorpay_payment_id,
              razorpay_signature: razorpayResult.razorpay_signature,
              gift_order_id: response.data.id,
            });
            toast.success('Payment successful!');
            onOrderComplete && onOrderComplete(response.data);
            onClose();
            window.location.href = '/payment-success?session_id=' + razorpayResult.razorpay_order_id;
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name || deliveryName,
          email: user.email || companyEmail,
          contact: user.phone || deliveryPhone,
        },
        theme: {
          color: '#1e3a5f',
        },
        modal: {
          ondismiss: function() {
            toast.info('Payment cancelled');
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const calculateTotal = () => {
    const baseAmount = gift?.price * quantity || 0;
    const hasCustomization = uploadedLogoUrl || companyName;
    const customizationFee = hasCustomization ? (50 * quantity) : 0;
    return {
      baseAmount,
      customizationFee,
      total: baseAmount + customizationFee
    };
  };
  
  // Step 1: Design Editor (Vistaprint-style)
  const renderStep1 = () => (
    <div className="space-y-4">
      {/* Editor Header */}
      <div className="flex items-center justify-between bg-slate-100 p-3 rounded-lg">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Palette className="w-5 h-5 text-navy-600" />
          Design Your Custom Gift
        </h3>
        <div className="text-sm text-slate-600">
          Drag to position • Resize with controls below
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Panel - Controls */}
        <div className="space-y-4 order-2 lg:order-1">
          {/* Logo Upload */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Upload Logo
            </h4>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
              className="hidden"
            />
            
            {!logoPreview ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-navy-500 hover:bg-slate-100 transition-all"
              >
                <ImageIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">Click to upload</p>
                <p className="text-xs text-slate-500">JPG, PNG, PDF</p>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-white p-2 rounded border">
                  <img src={logoPreview} alt="Logo" className="w-12 h-12 object-contain rounded" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{logoFile?.name}</p>
                    {isUploading && <p className="text-xs text-navy-600">Uploading...</p>}
                    {uploadedLogoUrl && <p className="text-xs text-green-600">✓ Uploaded</p>}
                  </div>
                  <button onClick={removeLogo} className="p-1 text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Logo Controls - Always visible when logo is uploaded */}
                <div className="space-y-3 pt-2 border-t bg-white p-3 rounded-lg">
                  {/* Size Control */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                        <Maximize2 className="w-3 h-3" /> Size
                      </Label>
                      <span className="text-xs text-slate-500">{logoScale}%</span>
                    </div>
                    <Slider
                      value={[logoScale]}
                      onValueChange={(v) => setLogoScale(v[0])}
                      min={10}
                      max={80}
                      step={1}
                      className="mt-1"
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>Small</span>
                      <span>Large</span>
                    </div>
                  </div>
                  
                  {/* Rotation Control */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                        <RotateCw className="w-3 h-3" /> Rotation
                      </Label>
                      <span className="text-xs text-slate-500">{logoRotation}°</span>
                    </div>
                    <Slider
                      value={[logoRotation]}
                      onValueChange={(v) => setLogoRotation(v[0])}
                      min={-180}
                      max={180}
                      step={5}
                      className="mt-1"
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>-180°</span>
                      <span>0°</span>
                      <span>180°</span>
                    </div>
                  </div>
                  
                  {/* Flip Controls */}
                  <div>
                    <Label className="text-xs font-medium text-slate-700 mb-2 block">Flip Direction</Label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setLogoFlipH(!logoFlipH)}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 transition-all ${
                          logoFlipH 
                            ? 'border-navy-600 bg-navy-50 text-navy-700' 
                            : 'border-slate-200 hover:border-slate-300 text-slate-600'
                        }`}
                      >
                        <FlipHorizontal className="w-4 h-4" />
                        <span className="text-xs font-medium">Horizontal</span>
                      </button>
                      <button
                        onClick={() => setLogoFlipV(!logoFlipV)}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 transition-all ${
                          logoFlipV 
                            ? 'border-navy-600 bg-navy-50 text-navy-700' 
                            : 'border-slate-200 hover:border-slate-300 text-slate-600'
                        }`}
                      >
                        <FlipVertical className="w-4 h-4" />
                        <span className="text-xs font-medium">Vertical</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Logo Color Options */}
                  <div>
                    <Label className="text-xs font-medium text-slate-700 mb-2 block">Logo Color</Label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setLogoColor('original')}
                        className={`flex-1 flex items-center justify-center gap-1 py-2 px-2 rounded-lg border-2 transition-all ${
                          logoColor === 'original' 
                            ? 'border-navy-600 bg-navy-50 text-navy-700' 
                            : 'border-slate-200 hover:border-slate-300 text-slate-600'
                        }`}
                      >
                        <div className="w-4 h-4 rounded-full bg-gradient-to-br from-red-500 via-green-500 to-blue-500"></div>
                        <span className="text-xs font-medium">Original</span>
                      </button>
                      <button
                        onClick={() => setLogoColor('black')}
                        className={`flex-1 flex items-center justify-center gap-1 py-2 px-2 rounded-lg border-2 transition-all ${
                          logoColor === 'black' 
                            ? 'border-navy-600 bg-navy-50 text-navy-700' 
                            : 'border-slate-200 hover:border-slate-300 text-slate-600'
                        }`}
                      >
                        <div className="w-4 h-4 rounded-full bg-black"></div>
                        <span className="text-xs font-medium">Black</span>
                      </button>
                      <button
                        onClick={() => setLogoColor('white')}
                        className={`flex-1 flex items-center justify-center gap-1 py-2 px-2 rounded-lg border-2 transition-all ${
                          logoColor === 'white' 
                            ? 'border-navy-600 bg-navy-50 text-navy-700' 
                            : 'border-slate-200 hover:border-slate-300 text-slate-600'
                        }`}
                      >
                        <div className="w-4 h-4 rounded-full bg-white border border-slate-300"></div>
                        <span className="text-xs font-medium">White</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Transparent Background Toggle */}
                  <div>
                    <Label className="text-xs font-medium text-slate-700 mb-2 block">Background</Label>
                    <button
                      onClick={() => setLogoTransparent(!logoTransparent)}
                      className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 transition-all ${
                        logoTransparent 
                          ? 'border-green-500 bg-green-50 text-green-700' 
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                    >
                      <Eye className="w-4 h-4" />
                      <span className="text-xs font-medium">
                        {logoTransparent ? '✓ Transparent (White removed)' : 'Show Original'}
                      </span>
                    </button>
                    <p className="text-xs text-slate-400 mt-1 text-center">
                      Toggle to remove white background from logo
                    </p>
                  </div>
                  
                  {/* Reset Button */}
                  <button
                    onClick={() => {
                      setLogoScale(30);
                      setLogoRotation(0);
                      setLogoFlipH(false);
                      setLogoFlipV(false);
                      setLogoTransparent(true);
                      setLogoColor('original');
                      setLogoPosition({ x: 50, y: 50 });
                    }}
                    className="w-full text-xs text-slate-500 hover:text-slate-700 py-1 underline"
                  >
                    Reset to default
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Company Name */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <Type className="w-4 h-4" />
              Company Name
            </h4>
            <Input
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                if (e.target.value) setActiveElement('text');
              }}
              placeholder="Enter company name"
              className="mb-2"
            />
            
            {companyName && activeElement === 'text' && (
              <div className="space-y-3 pt-2 border-t">
                <div>
                  <Label className="text-xs text-slate-600">Text Color</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {PRESET_COLORS.slice(0, 6).map((color) => (
                      <button
                        key={color}
                        onClick={() => setTextColor(color)}
                        className={`w-6 h-6 rounded border-2 ${textColor === color ? 'border-navy-600 ring-1 ring-navy-200' : 'border-slate-200'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="w-6 h-6 rounded cursor-pointer"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Text Size</Label>
                  <Select value={textSize} onValueChange={setTextSize}>
                    <SelectTrigger className="mt-1 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEXT_SIZES.map((size) => (
                        <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          
          {/* Brand Color */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Brand Color
            </h4>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => setBrandingColor(color)}
                  className={`w-8 h-8 rounded-lg border-2 ${brandingColor === color ? 'border-navy-600 ring-2 ring-navy-200' : 'border-slate-200'}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Center - Design Canvas */}
        <div className="lg:col-span-2 order-1 lg:order-2">
          <div className="bg-white rounded-xl border-2 border-slate-200 p-4">
            <div className="text-center text-xs text-slate-500 mb-2">
              👆 Click and drag logo/text to reposition
            </div>
            
            {/* Canvas Editor */}
            <div 
              ref={editorRef}
              className="relative bg-slate-100 rounded-lg overflow-hidden select-none"
              style={{ 
                aspectRatio: '1/1',
                maxHeight: '400px',
                cursor: (isDraggingLogo || isDraggingText) ? 'grabbing' : 'default',
                isolation: 'isolate'
              }}
              onClick={() => setActiveElement(null)}
            >
              {/* Product Image */}
              <img 
                src={gift?.images?.[0]} 
                alt={gift?.name} 
                className="w-full h-full object-cover"
                draggable={false}
              />
              
              {/* Draggable Logo - Using canvas-processed image */}
              {logoPreview && logoFile?.type !== 'application/pdf' && (
                <div
                  className="absolute"
                  style={{
                    left: `${logoPosition.x}%`,
                    top: `${logoPosition.y}%`,
                    transform: `translate(-50%, -50%) rotate(${logoRotation}deg) scaleX(${logoFlipH ? -1 : 1}) scaleY(${logoFlipV ? -1 : 1})`,
                    width: `${logoScale}%`,
                    zIndex: 10
                  }}
                >
                  {/* The canvas-processed logo image (white bg removed, color applied) */}
                  <img 
                    src={processedLogoUrl || logoPreview} 
                    alt="Logo" 
                    className="w-full h-auto object-contain cursor-grab active:cursor-grabbing"
                    onMouseDown={handleLogoMouseDown}
                    onTouchStart={(e) => handleTouchStart(e, 'logo')}
                    onClick={(e) => { e.stopPropagation(); setActiveElement('logo'); }}
                    style={{
                      touchAction: 'none'
                    }}
                    draggable={false}
                    ref={logoRef}
                  />
                  {/* Selection UI overlay */}
                  {activeElement === 'logo' && (
                    <>
                      {/* Dashed border */}
                      <div className="absolute inset-0 border-2 border-blue-500 border-dashed rounded pointer-events-none" style={{ margin: '-4px' }} />
                      {/* Drag label */}
                      <div 
                        className="absolute -top-7 left-1/2 bg-blue-500 text-white px-2 py-0.5 rounded text-xs flex items-center gap-1 whitespace-nowrap shadow-md pointer-events-none"
                        style={{ transform: `translateX(-50%) scaleX(${logoFlipH ? -1 : 1}) scaleY(${logoFlipV ? -1 : 1})` }}
                      >
                        <Move className="w-3 h-3" /> Drag to move
                      </div>
                      {/* Corner handles */}
                      <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow pointer-events-none"></div>
                      <div className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow pointer-events-none"></div>
                      <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow pointer-events-none"></div>
                      <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow pointer-events-none"></div>
                    </>
                  )}
                </div>
              )}
              
              {/* PDF Logo Placeholder */}
              {logoPreview && logoFile?.type === 'application/pdf' && (
                <div
                  onMouseDown={handleLogoMouseDown}
                  onTouchStart={(e) => handleTouchStart(e, 'logo')}
                  onClick={(e) => { e.stopPropagation(); setActiveElement('logo'); }}
                  className={`absolute cursor-grab active:cursor-grabbing ${activeElement === 'logo' ? 'ring-2 ring-blue-500' : ''}`}
                  style={{
                    left: `${logoPosition.x}%`,
                    top: `${logoPosition.y}%`,
                    transform: `translate(-50%, -50%)`,
                    width: `${logoScale}%`,
                    zIndex: 10,
                    touchAction: 'none'
                  }}
                >
                  <div className="bg-white rounded-lg shadow-lg p-3 flex flex-col items-center">
                    <FileText className="w-8 h-8 text-red-500" />
                    <span className="text-xs text-slate-600 mt-1">PDF Logo</span>
                  </div>
                </div>
              )}
              
              {/* Draggable Text */}
              {companyName && (
                <div
                  ref={textRef}
                  onMouseDown={handleTextMouseDown}
                  onTouchStart={(e) => handleTouchStart(e, 'text')}
                  onClick={(e) => { e.stopPropagation(); setActiveElement('text'); }}
                  className={`absolute cursor-grab active:cursor-grabbing whitespace-nowrap ${activeElement === 'text' ? 'ring-2 ring-green-500 ring-offset-2' : ''}`}
                  style={{
                    left: `${textPosition.x}%`,
                    top: `${textPosition.y}%`,
                    transform: 'translate(-50%, -50%)',
                    fontSize: TEXT_SIZES.find(s => s.value === textSize)?.fontSize || 20,
                    fontWeight: 'bold',
                    color: textColor,
                    textShadow: '2px 2px 4px rgba(255,255,255,0.9), -1px -1px 2px rgba(255,255,255,0.9)',
                    zIndex: 10,
                    padding: '4px 8px',
                    backgroundColor: activeElement === 'text' ? 'rgba(255,255,255,0.3)' : 'transparent',
                    borderRadius: '4px',
                    touchAction: 'none'
                  }}
                >
                  {companyName}
                  {/* Drag Handle Indicator */}
                  {activeElement === 'text' && (
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-green-500 text-white px-2 py-0.5 rounded text-xs flex items-center gap-1">
                      <Move className="w-3 h-3" /> Drag to move
                    </div>
                  )}
                </div>
              )}
              
              {/* Empty State */}
              {!logoPreview && !companyName && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-slate-500 bg-white/80 p-6 rounded-lg">
                    <ImageIcon className="w-12 h-12 mx-auto mb-2 text-slate-400" />
                    <p className="font-medium">Upload your logo or add text</p>
                    <p className="text-sm">to customize this gift</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Step 2: Delivery Details
  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <Truck className="w-5 h-5 text-navy-600" />
        Delivery Details
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label htmlFor="quantity" className="text-slate-900">
            Quantity (Min: {gift?.min_order_quantity || 1})
          </Label>
          <Input
            id="quantity"
            type="number"
            min={gift?.min_order_quantity || 1}
            value={quantity}
            onChange={(e) => setQuantity(Math.max(gift?.min_order_quantity || 1, parseInt(e.target.value) || 1))}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="deliveryName" className="text-slate-900">Recipient Name *</Label>
          <Input
            id="deliveryName"
            value={deliveryName}
            onChange={(e) => setDeliveryName(e.target.value)}
            placeholder="Full name"
            required
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="deliveryPhone" className="text-slate-900">Phone Number *</Label>
          <Input
            id="deliveryPhone"
            value={deliveryPhone}
            onChange={(e) => setDeliveryPhone(e.target.value)}
            placeholder="+91 98765 43210"
            required
            className="mt-1"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label htmlFor="companyEmail" className="text-slate-900">Company Email</Label>
          <Input
            id="companyEmail"
            type="email"
            value={companyEmail}
            onChange={(e) => setCompanyEmail(e.target.value)}
            placeholder="you@company.com"
            className="mt-1"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label htmlFor="deliveryAddress" className="text-slate-900">Delivery Address *</Label>
          <Textarea
            id="deliveryAddress"
            value={deliveryAddress}
            onChange={(e) => setDeliveryAddress(e.target.value)}
            placeholder="Full delivery address including pincode"
            rows={3}
            required
            className="mt-1"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label htmlFor="specialInstructions" className="text-slate-900">Special Instructions</Label>
          <Textarea
            id="specialInstructions"
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            placeholder="Any special instructions for the order..."
            rows={2}
            className="mt-1"
          />
        </div>
      </div>
    </div>
  );
  
  // Step 3: Confirm Order
  const renderStep3 = () => {
    const pricing = calculateTotal();
    
    return (
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-navy-600" />
          Order Confirmation
        </h3>
        
        {/* Preview Summary */}
        <div className="bg-slate-50 p-4 rounded-lg">
          <div className="flex gap-4">
            <div className="relative w-24 h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
              <img src={gift?.images?.[0]} alt={gift?.name} className="w-full h-full object-cover" />
              {(uploadedLogoUrl || companyName) && (
                <div className="absolute inset-0 bg-gold/20 flex items-center justify-center">
                  <span className="text-xs font-bold text-gold-dark bg-white/90 px-1 rounded">Customized</span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900">{gift?.name}</h4>
              <p className="text-sm text-slate-600">{gift?.category}</p>
              <p className="text-sm text-slate-600">Qty: {quantity}</p>
              {uploadedLogoUrl && <span className="inline-block mt-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Logo Added</span>}
              {companyName && <span className="inline-block mt-1 ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{companyName}</span>}
            </div>
          </div>
        </div>
        
        {/* Delivery Summary */}
        <div className="bg-slate-50 p-4 rounded-lg">
          <h4 className="font-medium text-slate-900 mb-2">Delivery To:</h4>
          <p className="text-slate-700">{deliveryName}</p>
          <p className="text-sm text-slate-600">{deliveryPhone}</p>
          <p className="text-sm text-slate-600 mt-1">{deliveryAddress}</p>
        </div>
        
        {/* Pricing */}
        <div className="bg-slate-50 p-4 rounded-lg">
          <h4 className="font-medium text-slate-900 mb-3">Price Breakdown</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">₹{gift?.price?.toLocaleString()} × {quantity}:</span>
              <span className="text-slate-900">₹{pricing.baseAmount.toLocaleString()}</span>
            </div>
            {pricing.customizationFee > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-600">Customization (₹50 × {quantity}):</span>
                <span className="text-slate-900">₹{pricing.customizationFee.toLocaleString()}</span>
              </div>
            )}
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span className="text-slate-900">Total:</span>
              <span className="text-navy-700 text-lg">₹{pricing.total.toLocaleString()}</span>
            </div>
          </div>
        </div>
        
        {/* Delivery Time */}
        <div className="flex items-center gap-2 text-sm text-slate-600 bg-blue-50 p-3 rounded-lg">
          <Truck className="w-4 h-4 text-blue-600" />
          Estimated Delivery: {gift?.delivery_time || '5-7 business days'}
        </div>
        
        {/* Test Mode */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="testMode" 
              checked={testMode}
              onCheckedChange={(checked) => setTestMode(checked)}
            />
            <Label htmlFor="testMode" className="flex items-center gap-2 cursor-pointer">
              <TestTube2 className="w-4 h-4 text-amber-600" />
              <span className="text-amber-800 font-medium">Test Mode</span>
            </Label>
          </div>
          {testMode && (
            <Input 
              type="number"
              min="1"
              value={customAmount}
              onChange={(e) => setCustomAmount(parseFloat(e.target.value) || 1)}
              className="bg-white border-amber-300"
              placeholder="Custom amount (₹)"
            />
          )}
        </div>
        
        {/* Payment Badge */}
        <div className="flex items-center justify-center gap-4 py-3 bg-gray-50 rounded-lg">
          <img src="https://cdn.razorpay.com/logo.svg" alt="Razorpay" className="h-5" />
          <span className="text-xs text-slate-500">Secure Payment</span>
        </div>
      </div>
    );
  };
  
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return true; // Customization is optional
      case 2:
        return deliveryName && deliveryPhone && deliveryAddress;
      case 3:
        return true;
      default:
        return false;
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900">
            {currentStep === 1 && 'Design Your Custom Gift'}
            {currentStep === 2 && 'Delivery Details'}
            {currentStep === 3 && 'Confirm Order'}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {gift?.name} - ₹{gift?.price?.toLocaleString()} per unit
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-4">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  currentStep >= step 
                    ? 'bg-navy-600 text-white' 
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {currentStep > step ? <Check className="w-4 h-4" /> : step}
              </div>
              <span className={`ml-2 text-sm hidden sm:inline ${currentStep >= step ? 'text-navy-600 font-medium' : 'text-slate-500'}`}>
                {step === 1 && 'Design'}
                {step === 2 && 'Delivery'}
                {step === 3 && 'Confirm'}
              </span>
              {step < 3 && <div className={`w-8 sm:w-16 h-0.5 mx-2 ${currentStep > step ? 'bg-navy-600' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>
        
        {/* Step Content */}
        <div className="min-h-[300px]">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>
        
        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : onClose()}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {currentStep > 1 ? 'Back' : 'Cancel'}
          </Button>
          
          {currentStep < totalSteps ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
              className="bg-navy-600 hover:bg-navy-700 text-white flex items-center gap-2"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmitOrder}
              disabled={isSubmitting || !canProceed()}
              className="bg-gold hover:bg-gold-light text-navy-900 font-semibold flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4" />
                  Place Order
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
